import React, { useState } from 'react';
import { Phone, MessageCircle, Send, X } from 'lucide-react';
import { SITE } from '../data/mock';

/**
 * Floating contact rail — call, WhatsApp, Telegram from any page.
 *
 * Single FAB by default (the call button, with the existing ring
 * animation) — visitors who only ever want to dial don't get a third
 * UI element to dismiss. Hover / focus expands a stacked rail with
 * WhatsApp and Telegram. Tap on mobile opens the rail with a toggle.
 *
 * Click-handlers fall through to standard `tel:` / `wa.me` / `t.me`
 * deep links so the OS handles routing — no JS-side state to drift.
 */
// Pulled out of SITE so the marketing team can later promote these to
// SITE if they want them centralised. For now they only show up here.
const WHATSAPP_E164 = '442033551151';
const TELEGRAM_USERNAME = 'voipinger';

export default function FloatingCallButton() {
  const [open, setOpen] = useState(false);

  const items = [
    {
      key: 'whatsapp',
      href: `https://wa.me/${WHATSAPP_E164}?text=${encodeURIComponent("Hi VoIPinger — I'd like to chat about your services.")}`,
      label: 'WhatsApp',
      Icon: MessageCircle,
      bg: 'bg-[#25D366]',
    },
    {
      key: 'telegram',
      href: `https://t.me/${TELEGRAM_USERNAME}`,
      label: 'Telegram',
      Icon: Send,
      bg: 'bg-[#229ED9]',
    },
  ];

  return (
    <div
      className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <div
        className={`flex flex-col items-end gap-3 transition-all duration-200 ${
          open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'
        }`}
        aria-hidden={!open}
      >
        {items.map(({ key, href, label, Icon, bg }) => (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            aria-label={`Chat with VoIPinger on ${label}`}
            className="group flex items-center gap-2"
          >
            <span className="hidden md:inline-block px-3 py-1.5 rounded-full bg-white text-slate-900 text-xs font-bold shadow-md border border-slate-200 group-hover:-translate-x-1 transition-transform">
              {label}
            </span>
            <span className={`w-12 h-12 rounded-full ${bg} flex items-center justify-center shadow-xl group-hover:scale-105 transition-transform`}>
              <Icon size={20} className="text-white" />
            </span>
          </a>
        ))}
      </div>

      {/* Primary call FAB — also acts as the open/close toggle on touch. */}
      <a
        href={`tel:${SITE.phoneTel}`}
        aria-label="Call VoIPinger"
        className="group relative"
        onClick={(e) => {
          // On a touch device the user expects a tap to surface options,
          // not auto-dial. We can't reliably detect touch — heuristic:
          // a pointerType=touch click never has a hover that opens the
          // rail first, so if the rail is closed we open it instead of
          // dialling.
          if (!open && window.matchMedia('(pointer: coarse)').matches) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className="absolute inset-0 rounded-full bg-violet-500/40 animate-ring" />
        <span className="absolute inset-0 rounded-full bg-violet-500/30 animate-ring [animation-delay:600ms]" />
        <span className="relative w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center shadow-2xl shadow-violet-500/40 group-hover:scale-105 transition-transform">
          {open ? <X size={22} className="text-white" /> : <Phone size={22} className="text-white" />}
        </span>
      </a>
    </div>
  );
}
